import { Link } from 'react-router-dom';
import { ArrowRight, Palette, Calendar, MessageCircle, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Landing = () => {
  const features = [
    {
      icon: Palette,
      title: 'Portfolio Digital',
      description: 'Muestra tu arte al mundo con una galería profesional'
    },
    {
      icon: Calendar,
      title: 'Gestión de Citas',
      description: 'Organiza tu agenda y gestiona reservas fácilmente'
    },
    {
      icon: MessageCircle,
      title: 'Chat Directo',
      description: 'Comunícate con clientes de forma privada y segura'
    },
    {
      icon: Share2,
      title: 'Redes Sociales',
      description: 'Comparte tu trabajo directamente a tus redes'
    }
  ];

  return (
    <div className="min-h-screen bg-zinc-950 overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1635068247786-5de1ce4c0ff8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB0YXR0b28lMjBzaG9wJTIwaW50ZXJpb3IlMjBkYXJrJTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3NjcwMTc1Njl8MA&ixlib=rb-4.1.0&q=85')`
          }}
        >
          <div className="absolute inset-0 bg-zinc-950/80" />
        </div>

        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 sm:px-6 md:px-12 py-4 sm:py-6">
          <span className="font-['Cinzel'] text-2xl font-bold tracking-wider text-white" data-testid="landing-logo">
            INKER
          </span>
          <Link to="/auth">
            <Button
              variant="ghost"
              className="text-zinc-400 hover:text-white hover:bg-transparent uppercase tracking-widest text-xs"
              data-testid="login-link"
            >
              Iniciar Sesión
            </Button>
          </Link>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] text-zinc-500 mb-4 sm:mb-6 animate-fade-in">
            La red social para tatuadores
          </p>
          <h1 className="font-['Cinzel'] text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter text-white mb-6 sm:mb-8 animate-fade-in stagger-1">
            TU ARTE.<br />TU COMUNIDAD.
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-zinc-400 mb-8 sm:mb-12 max-w-2xl mx-auto animate-fade-in stagger-2">
            Conecta con clientes, gestiona tu agenda y comparte tu trabajo con la comunidad de tatuadores más grande.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in stagger-3">
            <Link to="/auth">
              <Button
                className="bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold px-10 py-6 h-auto"
                data-testid="get-started-btn"
              >
                Comenzar Ahora
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/auth">
              <Button
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:border-zinc-300 hover:text-white bg-transparent uppercase tracking-widest text-xs px-10 py-6 h-auto"
                data-testid="explore-btn"
              >
                Explorar Artistas
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-zinc-600">Descubre más</span>
          <div className="w-px h-12 bg-gradient-to-b from-zinc-600 to-transparent" />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Características</p>
            <h2 className="font-['Cinzel'] text-4xl md:text-5xl font-bold text-white">
              TODO LO QUE NECESITAS
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="bg-zinc-900/50 border border-zinc-800/50 hover:border-zinc-700 transition-colors p-8 group"
                data-testid={`feature-${index}`}
              >
                <feature.icon className="w-8 h-8 text-zinc-500 group-hover:text-white transition-colors mb-6" />
                <h3 className="font-['Cinzel'] text-lg font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-sm text-zinc-500">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-24 px-6 md:px-12 bg-zinc-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Galería</p>
            <h2 className="font-['Cinzel'] text-4xl md:text-5xl font-bold text-white">
              ARTE QUE INSPIRA
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
            <div className="aspect-[4/5] overflow-hidden group">
              <img
                src="https://images.unsplash.com/photo-1552627019-947c3789ffb5?crop=entropy&cs=srgb&fm=jpg&w=600"
                alt="Tattoo art"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
              />
            </div>
            <div className="aspect-[4/5] overflow-hidden group">
              <img
                src="https://images.unsplash.com/photo-1568515045052-f9a854d70bfd?crop=entropy&cs=srgb&fm=jpg&w=600"
                alt="Tattoo art"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
              />
            </div>
            <div className="aspect-[4/5] overflow-hidden group hidden md:block">
              <img
                src="https://images.unsplash.com/photo-1595747644932-abb68f85f419?crop=entropy&cs=srgb&fm=jpg&w=600"
                alt="Tattoo studio"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Cinzel'] text-4xl md:text-6xl font-bold text-white mb-8">
            ÚNETE A LA COMUNIDAD
          </h2>
          <p className="text-lg text-zinc-400 mb-12 max-w-xl mx-auto">
            Miles de tatuadores ya están compartiendo su arte y conectando con nuevos clientes.
          </p>
          <Link to="/auth">
            <Button
              className="bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold px-12 py-6 h-auto"
              data-testid="join-btn"
            >
              Crear Cuenta Gratis
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 py-8 px-6 md:px-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="font-['Cinzel'] text-lg font-bold tracking-wider text-zinc-600">
            INKER
          </span>
          <p className="text-sm text-zinc-600">
            © 2026 Inker. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
